import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Receipt, CreditCard, DollarSign, Pencil, Trash2, RefreshCw, BedDouble } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';
import { format, differenceInDays, parseISO } from 'date-fns';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const BillingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { rooms, roomTypes } = useHotel();
  const [invoices, setInvoices] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ reservation_id: '', guest_id: '', tax_percent: 0, discount_percent: 0, items: [{ description: 'Room Charges', quantity: 1, unit_price: 0, total: 0 }] });
  const [paymentForm, setPaymentForm] = useState({ invoice_id: '', amount: '', method: 'card', reference: '' });

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (!user?.hotel_id) return;
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [invoiceRes, resRes, guestRes] = await Promise.all([
        axios.get(`${API}/hotels/${user.hotel_id}/invoices`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/reservations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/guests`, { headers: getAuthHeaders() })
      ]);
      setInvoices(invoiceRes.data);
      setReservations(resRes.data);
      setGuests(guestRes.data);
      setLastUpdated(new Date());
    } catch (error) { 
      console.error('Failed:', error); 
    } finally { 
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.hotel_id, getAuthHeaders]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    const intervalId = setInterval(() => fetchData(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const handleManualRefresh = () => fetchData(true);

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      if (!invoiceForm.guest_id) {
        toast.error('Please select a reservation first');
        return;
      }
      const items = invoiceForm.items.map(item => ({
        ...item,
        unit_price: parseFloat(item.unit_price) || 0,
        total: (parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 1)
      }));
      const subtotal = items.reduce((sum, item) => sum + item.total, 0);
      const taxPercent = parseFloat(invoiceForm.tax_percent) || 0;
      const discountPercent = parseFloat(invoiceForm.discount_percent) || 0;
      const tax_amount = subtotal * (taxPercent / 100);
      const discountAmount = subtotal * (discountPercent / 100);
      const total_amount = subtotal + tax_amount - discountAmount;

      const payload = {
        guest_id: invoiceForm.guest_id,
        reservation_id: invoiceForm.reservation_id || null,
        items,
        subtotal,
        tax_rate: taxPercent,
        tax_amount,
        total_amount
      };
      
      if (editingInvoice) {
        const response = await axios.put(`${API}/hotels/${user.hotel_id}/invoices/${editingInvoice.id}`, payload, { headers: getAuthHeaders() });
        setInvoices(prev => prev.map(inv => inv.id === editingInvoice.id ? response.data : inv));
        toast.success('Invoice updated');
      } else {
        const response = await axios.post(`${API}/hotels/${user.hotel_id}/invoices`, payload, { headers: getAuthHeaders() });
        setInvoices(prev => [response.data, ...prev]);
        toast.success('Invoice created');
      }
      setShowInvoiceDialog(false);
      resetInvoiceForm();
    } catch (error) { toast.error(getErrorMessage(error, 'Failed')); }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      reservation_id: invoice.reservation_id || '',
      guest_id: invoice.guest_id,
      tax_percent: invoice.tax_percent !== undefined ? invoice.tax_percent : 0,
      discount_percent: invoice.discount_percent || 0,
      items: invoice.items || [{ description: 'Room Charges', quantity: 1, unit_price: 0, total: 0 }]
    });
    setShowInvoiceDialog(true);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/invoices/${invoiceId}`, { headers: getAuthHeaders() });
      setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      toast.success('Invoice deleted');
    } catch (error) { toast.error(getErrorMessage(error, 'Failed to delete')); }
  };

  const resetInvoiceForm = () => {
    setEditingInvoice(null);
    setInvoiceForm({ reservation_id: '', guest_id: '', tax_percent: 0, discount_percent: 0, items: [{ description: 'Room Charges', quantity: 1, unit_price: 0, total: 0 }] });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hotels/${user.hotel_id}/payments`, { ...paymentForm, amount: parseFloat(paymentForm.amount) }, { headers: getAuthHeaders() });
      const response = await axios.get(`${API}/hotels/${user.hotel_id}/invoices`, { headers: getAuthHeaders() });
      setInvoices(response.data);
      setShowPaymentDialog(false);
      toast.success('Payment recorded');
    } catch (error) { toast.error(getErrorMessage(error, 'Failed')); }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({ invoice_id: invoice.id, amount: (getInvoiceTotal(invoice) - (invoice.paid_amount || 0)).toFixed(2), method: 'card', reference: '' });
    setShowPaymentDialog(true);
  };

  const getGuestName = (invoice) => {
    if (invoice.guest_name) return invoice.guest_name;
    if (invoice.guest_id) {
      const g = guests.find(g => g.id === invoice.guest_id);
      return g ? `${g.first_name} ${g.last_name}` : '—';
    }
    return '—';
  };

  const getInvoiceTotal = (invoice) => {
    if (invoice.total_amount && invoice.total_amount > 0) return invoice.total_amount;
    const itemsTotal = (invoice.items || []).reduce((s, i) => s + (i.total || (i.unit_price || 0) * (i.quantity || 1)), 0);
    const tax = itemsTotal * ((invoice.tax_rate || 0) / 100);
    return itemsTotal + tax;
  };
  const getStatusBadge = (status) => { const s = { pending: 'bg-yellow-100 text-yellow-700', partial: 'bg-blue-100 text-blue-700', paid: 'bg-emerald-100 text-emerald-700' }; return s[status] || 'bg-gray-100 text-gray-700'; };
  
  // Filter reservations that are checked-in (revenue counted at check-in, when guest paid)
  const checkedInRes = reservations.filter(r => r.status === 'checked_in');
  
  // Calculate nights stayed for a reservation
  const calculateNights = (checkIn, checkOut) => {
    try {
      const start = parseISO(checkIn);
      const end = parseISO(checkOut);
      const nights = differenceInDays(end, start);
      return nights > 0 ? nights : 1;
    } catch {
      return 1;
    }
  };

  // Get room rate for a reservation
  const getRoomRate = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return 100; // default
    const roomType = roomTypes?.find(rt => rt.id === room.room_type_id);
    return roomType?.base_rate || 100;
  };

  // Calculate total revenue from checked-in reservations (check-in = paid)
  // Revenue is counted when guest checks in, not at checkout
  const totalReservationRevenue = reservations
    .filter(r => ['checked_in', 'checked_out'].includes(r.status))
    .reduce((sum, res) => {
      // Use total_amount if set, otherwise calculate from room rate and nights
      if (res.total_amount && res.total_amount > 0) {
        return sum + res.total_amount;
      }
      const nights = calculateNights(res.check_in_date, res.check_out_date);
      const rate = res.rate_per_night || getRoomRate(res.room_id);
      return sum + (rate * nights);
    }, 0);

  // All revenue is collected at check-in (no outstanding since they pay when checking in)
  const totalCollected = totalReservationRevenue;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Billing & Payments</h1>
          <p className="text-muted-foreground">
            Manage invoices and payments
            {lastUpdated && <span className="ml-2 text-xs">• Updated: {format(lastUpdated, 'HH:mm:ss')}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Dialog open={showInvoiceDialog} onOpenChange={(open) => { setShowInvoiceDialog(open); if (!open) resetInvoiceForm(); }}>
            <DialogTrigger asChild><Button data-testid="create-invoice-btn" disabled={checkedInRes.length === 0}><Plus className="h-4 w-4 mr-2" />Create Invoice</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editingInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateInvoice} className="space-y-4">
                <div className="space-y-2"><Label>Reservation *</Label>
                  <Select value={invoiceForm.reservation_id} onValueChange={(value) => { 
                    const res = reservations.find(r => r.id === value); 
                    if (res) {
                      const nights = calculateNights(res.check_in_date, res.check_out_date);
                      const rate = res.rate_per_night || getRoomRate(res.room_id);
                      const amount = res.total_amount > 0 ? res.total_amount : (rate * nights);
                      setInvoiceForm(prev => ({ ...prev, reservation_id: value, guest_id: res.guest_id, items: [{ description: `Room Charges (${nights} night${nights > 1 ? 's' : ''} @ €${rate}/night)`, quantity: nights, unit_price: rate, total: amount }] })); 
                    }
                  }}>
                    <SelectTrigger data-testid="invoice-reservation-select"><SelectValue placeholder="Select reservation" /></SelectTrigger>
                    <SelectContent>{checkedInRes.map((r) => { 
                      const room = rooms.find(rm => rm.id === r.room_id); 
                      const nights = calculateNights(r.check_in_date, r.check_out_date);
                      return (<SelectItem key={r.id} value={r.id}>{getGuestName({guest_id: r.guest_id})} - Room {room?.number || room?.room_number} ({nights} nights)</SelectItem>); 
                    })}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Tax %</Label><Input type="number" value={invoiceForm.tax_percent} onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_percent: e.target.value })} data-testid="invoice-tax-input" /></div>
                  <div className="space-y-2"><Label>Discount %</Label><Input type="number" value={invoiceForm.discount_percent} onChange={(e) => setInvoiceForm({ ...invoiceForm, discount_percent: e.target.value })} data-testid="invoice-discount-input" /></div>
                </div>
                {invoiceForm.items[0]?.total > 0 && (
                  <div className="p-3 bg-muted rounded-lg space-y-1">
                    <p className="text-sm font-medium">Invoice Preview</p>
                    {(() => {
                      const subtotal = invoiceForm.items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 1), 0);
                      const tax = subtotal * ((parseFloat(invoiceForm.tax_percent) || 0) / 100);
                      const discount = subtotal * ((parseFloat(invoiceForm.discount_percent) || 0) / 100);
                      const total = subtotal + tax - discount;
                      return (
                        <>
                          <div className="flex justify-between text-sm"><span>Subtotal</span><span>€{subtotal.toFixed(2)}</span></div>
                          {tax > 0 && <div className="flex justify-between text-sm"><span>Tax ({invoiceForm.tax_percent}%)</span><span>€{tax.toFixed(2)}</span></div>}
                          {discount > 0 && <div className="flex justify-between text-sm text-green-600"><span>Discount ({invoiceForm.discount_percent}%)</span><span>-€{discount.toFixed(2)}</span></div>}
                          <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>€{total.toFixed(2)}</span></div>
                        </>
                      );
                    })()}
                  </div>
                )}
                <Button type="submit" className="w-full" data-testid="submit-invoice-btn">{editingInvoice ? 'Update Invoice' : 'Create Invoice'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Revenue</p><p className="text-2xl font-bold text-emerald-600">€{totalReservationRevenue.toFixed(2)}</p><p className="text-xs text-muted-foreground">From {reservations.filter(r => ['checked_in', 'checked_out'].includes(r.status)).length} check-in{reservations.filter(r => ['checked_in', 'checked_out'].includes(r.status)).length !== 1 ? 's' : ''}</p></div><div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950"><DollarSign className="h-5 w-5 text-emerald-600" /></div></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">In-House Guests</p><p className="text-2xl font-bold text-blue-600">{checkedInRes.length}</p><p className="text-xs text-muted-foreground">Currently checked in</p></div><div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950"><BedDouble className="h-5 w-5 text-blue-600" /></div></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Pending Arrivals</p><p className="text-2xl font-bold text-amber-600">{reservations.filter(r => ['pending', 'confirmed'].includes(r.status)).length}</p><p className="text-xs text-muted-foreground">Awaiting check-in</p></div><div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950"><Receipt className="h-5 w-5 text-amber-600" /></div></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50"><tr><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Invoice #</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Guest</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Paid</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th></tr></thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b hover:bg-muted/30" data-testid={`invoice-row-${invoice.id}`}>
                    <td className="py-3 px-4 font-mono text-sm">{invoice.invoice_number}</td>
                    <td className="py-3 px-4">{getGuestName(invoice)}</td>
                    <td className="py-3 px-4 font-medium">€{getInvoiceTotal(invoice).toFixed(2)}</td>
                    <td className="py-3 px-4 text-emerald-600">€{invoice.paid_amount?.toFixed(2)}</td>
                    <td className="py-3 px-4"><Badge className={getStatusBadge(invoice.status)}>{invoice.status}</Badge></td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        {invoice.status !== 'paid' && <Button size="sm" variant="outline" onClick={() => openPaymentDialog(invoice)} data-testid={`pay-invoice-${invoice.id}`}><CreditCard className="h-4 w-4 mr-1" />Pay</Button>}
                        <Button size="sm" variant="ghost" onClick={() => handleEditInvoice(invoice)} data-testid={`edit-invoice-${invoice.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteInvoice(invoice.id)} data-testid={`delete-invoice-${invoice.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {invoices.length === 0 && <p className="text-center text-muted-foreground py-8">No invoices yet.</p>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            {selectedInvoice && <div className="p-4 bg-muted rounded-lg"><p className="font-medium">{selectedInvoice.invoice_number}</p><p className="text-sm text-muted-foreground">Due: €{(getInvoiceTotal(selectedInvoice) - (selectedInvoice.paid_amount || 0)).toFixed(2)}</p></div>}
            <div className="space-y-2"><Label>Amount (€) *</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="payment-amount-input" required /></div>
            <div className="space-y-2"><Label>Payment Method</Label>
              <Select value={paymentForm.method} onValueChange={(value) => setPaymentForm({ ...paymentForm, method: value })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" data-testid="submit-payment-btn">Record Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
